import React from 'react';
import type { OBConsultMeasurements, Pregnancy, Presentation } from '../types/pregnancy';

interface Props {
  measurements: OBConsultMeasurements;
  onChange: (m: OBConsultMeasurements) => void;
  pregnancies: Pregnancy[];
  selectedPregnancyId: string;
  onSelectPregnancy: (id: string) => void;
  onNewPregnancy: () => void;
}

/**
 * Section 1 of the obstetric consultation form — structured gravidogram fields.
 * Section 2 (SOAP notes) reuses the existing SOAPNoteEditor.
 */
export function OBConsultMeasurementsForm({
  measurements: m, onChange, pregnancies, selectedPregnancyId, onSelectPregnancy, onNewPregnancy,
}: Props) {
  const set = <K extends keyof OBConsultMeasurements>(key: K, value: OBConsultMeasurements[K]) =>
    onChange({ ...m, [key]: value });

  const activePregnancies = pregnancies.filter(p => p.status === 'active');

  return (
    <div className="mb-4 bg-white rounded-lg shadow-sm border border-teal-200 p-4">
      <h3 className="text-sm font-semibold text-teal-700 uppercase tracking-wider mb-3">Obstetric Measurements</h3>

      {/* Pregnancy selector */}
      <div className="mb-3">
        <label className="block text-xs font-medium text-gray-500 mb-1">Pregnancy</label>
        {activePregnancies.length === 0 ? (
          <div className="flex items-center gap-2">
            <span className="text-sm text-amber-700">No active pregnancy</span>
            <button onClick={onNewPregnancy} className="text-sm text-blue-600 hover:text-blue-800 underline">Create one</button>
          </div>
        ) : (
          <select
            value={selectedPregnancyId}
            onChange={e => onSelectPregnancy(e.target.value)}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-white"
          >
            <option value="">— Select pregnancy —</option>
            {activePregnancies.map(p => (
              <option key={p.pregnancyId} value={p.pregnancyId}>
                EDD: {p.edd} — G{p.gravidity}P{p.parity} ({p.gestationEstablishedBy})
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Row 1: Gestation */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Gestation (weeks)</label>
          <input type="number" min={0} max={45} value={m.gestationWeeks ?? ''} onChange={e => set('gestationWeeks', e.target.value ? parseInt(e.target.value) : null)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Days (0–6)</label>
          <input type="number" min={0} max={6} value={m.gestationDays ?? ''} onChange={e => set('gestationDays', e.target.value ? parseInt(e.target.value) : null)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
        </div>
      </div>

      {/* Row 2: BP */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">BP Systolic (mmHg)</label>
          <input type="number" min={0} max={300} value={m.bpSystolic ?? ''} onChange={e => set('bpSystolic', e.target.value ? parseInt(e.target.value) : null)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">BP Diastolic (mmHg)</label>
          <input type="number" min={0} max={200} value={m.bpDiastolic ?? ''} onChange={e => set('bpDiastolic', e.target.value ? parseInt(e.target.value) : null)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
        </div>
      </div>

      {/* Row 3: SFH, FHR, Fetal Movements */}
      <div className="grid grid-cols-3 gap-3 mb-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">SFH (cm)</label>
          <input type="number" min={0} max={50} step={0.5} value={m.sfh ?? ''} onChange={e => set('sfh', e.target.value ? parseFloat(e.target.value) : null)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">FHR (bpm)</label>
          <input type="number" min={0} max={250} value={m.fetalHeartRate ?? ''} onChange={e => set('fetalHeartRate', e.target.value ? parseInt(e.target.value) : null)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
        </div>
        <div className="flex items-end pb-1">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={m.fetalMovements ?? false} onChange={e => set('fetalMovements', e.target.checked)} className="h-4 w-4 rounded" />
            Fetal Movements
          </label>
        </div>
      </div>

      {/* Row 4: Presentation + Head Above Brim */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Presentation</label>
          <select value={m.presentation} onChange={e => set('presentation', e.target.value as Presentation)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-white">
            <option value="">Not yet assessable</option>
            <option value="cephalic">Cephalic</option>
            <option value="breech">Breech</option>
            <option value="transverse">Transverse</option>
            <option value="oblique">Oblique</option>
          </select>
        </div>
        {m.presentation === 'cephalic' && (
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Head Above Brim (fifths 0–5)</label>
            <input type="number" min={0} max={5} value={m.headAboveBrim ?? ''} onChange={e => set('headAboveBrim', e.target.value ? parseInt(e.target.value) : null)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
          </div>
        )}
      </div>

      {/* Row 5: Haemoglobin, Urine */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Haemoglobin (g/dl)</label>
          <input type="number" min={0} max={20} step={0.1} value={m.haemoglobin ?? ''} onChange={e => set('haemoglobin', e.target.value ? parseFloat(e.target.value) : null)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Urine</label>
          <input value={m.urine} onChange={e => set('urine', e.target.value)} placeholder="e.g. NAD" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
        </div>
      </div>

      {/* Row 6: Supplements + TB Screen */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <label className="flex items-center gap-1.5 text-sm text-gray-700">
          <input type="checkbox" checked={m.supplements.iron} onChange={e => set('supplements', { ...m.supplements, iron: e.target.checked })} className="h-4 w-4 rounded" />
          Iron
        </label>
        <label className="flex items-center gap-1.5 text-sm text-gray-700">
          <input type="checkbox" checked={m.supplements.folate} onChange={e => set('supplements', { ...m.supplements, folate: e.target.checked })} className="h-4 w-4 rounded" />
          Folate
        </label>
        <label className="flex items-center gap-1.5 text-sm text-gray-700">
          <input type="checkbox" checked={m.supplements.calcium} onChange={e => set('supplements', { ...m.supplements, calcium: e.target.checked })} className="h-4 w-4 rounded" />
          Calcium
        </label>
        <span className="mx-2 text-gray-300">|</span>
        <label className="flex items-center gap-1.5 text-sm text-gray-700">
          <input type="checkbox" checked={m.tbScreenDone} onChange={e => set('tbScreenDone', e.target.checked)} className="h-4 w-4 rounded" />
          TB Screen Done
        </label>
      </div>

      {/* Row 7: Next Visit Date */}
      <div className="mb-1">
        <label className="block text-xs font-medium text-gray-500 mb-1">Next Visit Date</label>
        <input type="date" value={m.nextVisitDate ?? ''} onChange={e => set('nextVisitDate', e.target.value || null)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
      </div>
    </div>
  );
}
